import { format } from 'date-fns'
import { Link as RouterLink } from 'react-router-dom'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Skeleton } from '@/components/ui/skeleton'
import {
  APPOINTMENTS_DATE_PARAM,
  APPOINTMENTS_VIEW_PARAM,
} from '@/features/appointments/hooks/useAppointmentsViewState'
import type { AppointmentRecord } from '@/features/appointments/types'
import type { ClientHistory } from '@/features/patients/hooks/useClientHistory'
import {
  appointmentDate,
  splitAppointments,
} from '@/features/patients/utils/clientHistory'
import { ClientAppointmentRow } from './ClientAppointmentRow'
import { SHEET_TABLE_HEAD } from './sheetTableStyles'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

function AppointmentTable({
  title,
  appointments,
  now,
  action,
}: {
  title: string
  appointments: AppointmentRecord[]
  now: Date
  action?: React.ReactNode
}) {
  if (appointments.length === 0) return null
  return (
    <SheetSectionCard
      title={
        <span className="flex items-center justify-between gap-3">
          <span>{`${title} · ${appointments.length}`}</span>
          {action}
        </span>
      }
      body={
        // Fixed widths so the Upcoming and Past tables line up.
        <Table className="table-fixed">
          <colgroup>
            <col className="w-40" />
            <col className="w-24" />
            <col />
            <col className="w-28" />
            <col className="w-16" />
          </colgroup>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={SHEET_TABLE_HEAD}>Date</TableHead>
              <TableHead className={SHEET_TABLE_HEAD}>Type</TableHead>
              <TableHead className={SHEET_TABLE_HEAD}>Reason</TableHead>
              <TableHead className={SHEET_TABLE_HEAD}>Provider</TableHead>
              <TableHead className={SHEET_TABLE_HEAD}>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {appointments.map((appointment) => (
              <ClientAppointmentRow
                key={appointment.id}
                appointment={appointment}
                now={now}
              />
            ))}
          </TableBody>
        </Table>
      }
    />
  )
}

/**
 * Every appointment on file for the client, upcoming first. Cancelled and
 * no-show appointments stay listed, struck through.
 */
export function ClientAppointmentsTab({
  history,
  now,
}: {
  history: ClientHistory
  now: Date
}) {
  const { appointments } = history
  if (!appointments.allowed) return null

  if (appointments.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    )
  }

  if (appointments.isError || appointments.rows.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        {appointments.isError
          ? 'Could not load appointments. Try reopening the client.'
          : 'No appointments on file'}
      </p>
    )
  }

  const { upcoming, past } = splitAppointments(appointments.rows, now)
  const firstUpcoming = upcoming[0] ? appointmentDate(upcoming[0]) : null
  const calendarHref = firstUpcoming
    ? `/data/appointments?${APPOINTMENTS_VIEW_PARAM}=calendar&${APPOINTMENTS_DATE_PARAM}=${format(firstUpcoming, 'yyyy-MM-dd')}`
    : null

  return (
    <div className="flex flex-col gap-4">
      <AppointmentTable
        title="Upcoming"
        appointments={upcoming}
        now={now}
        action={
          calendarHref ? (
            // Opens the org calendar on the day of the client's next appointment.
            <RouterLink
              to={calendarHref}
              className="text-primary-low-contrast rounded-sm tracking-normal hover:underline focus-visible:ring-primary-solid focus-visible:ring-2 focus-visible:outline-none">
              Open calendar
            </RouterLink>
          ) : null
        }
      />
      <AppointmentTable title="Past" appointments={past} now={now} />
      {appointments.truncated ? (
        <p className="text-muted-foreground text-center text-xs">
          Showing the most recent {appointments.rows.length} appointments.
        </p>
      ) : null}
    </div>
  )
}
